import { apiService } from './api';
import { Companion, CreateCompanionData, PaginatedResponse } from '../types';
import { isMockMode, mockDelay, mockLog } from '../utils/mockHelper';
import { getMockCompanions, addMockCompanion, updateMockCompanion, deleteMockCompanion } from '../data/mockData';

export class CompanionService {
  async getCompanions(): Promise<Companion[]> {
    if (isMockMode()) {
      return this.mockGetCompanions();
    }
    
    try {
      console.log('🔄 CompanionService: Fetching companions...');
      const response = await apiService.get<Companion[]>('/companions');
      console.log('✅ CompanionService: Companions fetched successfully:', response.data);
      
      // Check if response.data is an array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // If it's a paginated response, extract the data array
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        return (response.data as any).data || [];
      }
      
      // Fallback to empty array
      return [];
    } catch (error: any) {
      console.error('❌ CompanionService: Error fetching companions:', error);
      
      // If the API endpoint doesn't exist (404), return empty array instead of throwing
      if (error.response?.status === 404) {
        console.log('⚠️ CompanionService: Companions endpoint not found, returning empty array');
        return [];
      }
      
      throw new Error(error.response?.data?.message || 'Failed to fetch companions');
    }
  }

  private async mockGetCompanions(): Promise<Companion[]> {
    mockLog('get companions');
    await mockDelay();
    return getMockCompanions();
  }

  async getCompanion(id: number): Promise<Companion> {
    if (isMockMode()) {
      return this.mockGetCompanion(id);
    }
    
    try {
      const response = await apiService.get<Companion>(`/companions/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch companion');
    }
  }

  private async mockGetCompanion(id: number): Promise<Companion> {
    mockLog('get companion', id);
    await mockDelay(200, 400);
    
    const companions = getMockCompanions();
    const companion = companions.find(c => c.id === id);
    
    if (!companion) {
      throw new Error('Acompanhante não encontrado');
    }
    
    return companion;
  }

  async createCompanion(companionData: CreateCompanionData): Promise<Companion> {
    if (isMockMode()) {
      return this.mockCreateCompanion(companionData);
    }
    
    try {
      const response = await apiService.post<any>('/companions', companionData);
      
      // Handle both direct companion object and wrapped response
      if (response.data.companion) {
        return response.data.companion;
      }
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create companion';
      const errors = error.response?.data?.errors;
      
      if (errors) {
        const errorMessages = Object.values(errors).flat().join(', ');
        throw new Error(`${message}: ${errorMessages}`);
      }
      
      throw new Error(message);
    }
  }

  private async mockCreateCompanion(companionData: CreateCompanionData): Promise<Companion> {
    mockLog('create companion', companionData);
    await mockDelay(600, 900);
    
    const newCompanion = addMockCompanion({
      name: companionData.name,
      email: companionData.email,
      document_number: companionData.document_number,
      date_of_birth: companionData.date_of_birth,
      phone: companionData.phone,
      country_code: companionData.country_code,
      // Calculate age from date of birth
      age: this.calculateAge(companionData.date_of_birth)
    });
    
    return newCompanion;
  }

  async updateCompanion(id: number, companionData: CreateCompanionData): Promise<Companion> {
    if (isMockMode()) {
      return this.mockUpdateCompanion(id, companionData);
    }
    
    try {
      const response = await apiService.put<any>(`/companions/${id}`, companionData);
      
      // Handle both direct companion object and wrapped response
      if (response.data.companion) {
        return response.data.companion;
      }
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update companion';
      const errors = error.response?.data?.errors;
      
      if (errors) {
        const errorMessages = Object.values(errors).flat().join(', ');
        throw new Error(`${message}: ${errorMessages}`);
      }
      
      throw new Error(message);
    }
  }

  private async mockUpdateCompanion(id: number, companionData: CreateCompanionData): Promise<Companion> {
    mockLog('update companion', { id, companionData });
    await mockDelay(600, 900);
    
    const updatedCompanion = updateMockCompanion(id, {
      name: companionData.name,
      email: companionData.email,
      document_number: companionData.document_number,
      date_of_birth: companionData.date_of_birth,
      phone: companionData.phone,
      country_code: companionData.country_code,
      age: this.calculateAge(companionData.date_of_birth)
    });
    
    if (!updatedCompanion) {
      throw new Error('Acompanhante não encontrado');
    }
    
    return updatedCompanion;
  }

  async deleteCompanion(id: number): Promise<void> {
    if (isMockMode()) {
      return this.mockDeleteCompanion(id);
    }
    
    try {
      await apiService.delete(`/companions/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete companion');
    }
  }

  private async mockDeleteCompanion(id: number): Promise<void> {
    mockLog('delete companion', id);
    await mockDelay(400, 600);
    
    const success = deleteMockCompanion(id);
    if (!success) {
      throw new Error('Acompanhante não encontrado');
    }
  }

  private calculateAge(birthDate: string): number {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  }
}

export const companionService = new CompanionService();
